import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  getDocs, 
  addDoc, 
  deleteDoc, 
  updateDoc,
  doc,
  where 
} from 'firebase/firestore';
// Removed Firebase Storage imports as we're using data URLs
import { db } from '../firebase';
import { 
  Container, 
  Card, 
  Form, 
  Button, 
  Row, 
  Col, 
  Table, 
  Alert, 
  Tabs, 
  Tab
} from 'react-bootstrap';
import CompanyManager from './CompanyManager'; // Import the CompanyManager component
import SimpleDatePicker from './SimpleDatePicker'; // Import the custom date picker

/**
 * AdminPanel component - For managing company shift patterns
 * Allows admins to add, edit and delete company shift patterns
 */
const AdminPanel = ({ currentUser }) => {
  // Check if user is admin - in a real app, you'd have proper role-based access control
  const [isAdmin, setIsAdmin] = useState(false);
  
  // State variables for the company form
  const [companies, setCompanies] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [patterns, setPatterns] = useState([]); // Used in fetchCompanies function
  const [shifts, setShifts] = useState([]);
  const [message, setMessage] = useState({ text: '', type: '' });
  
  // Form state
  const [formData, setFormData] = useState({
    company: '',
    patternName: '',
    shift: '',
    startDate: new Date().toISOString().split('T')[0],
    patternLength: 8, // Changed default from 28 to 8 days
    patternValues: Array(8).fill('X'), // Changed default from 28 to 8 days
    logoUrl: '' // Add logo URL field
  });
  
  // Image upload state - only using preview for UI
  const [, setLogoFile] = useState(null); // logoFile not used but setter is needed
  const [logoPreview, setLogoPreview] = useState(null);
  const [isUploading] = useState(false); // Upload state tracking
  
  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('add');

  // Fetch existing companies on component mount
  useEffect(() => {
    // Check if user is admin
    if (currentUser && currentUser.email) {
      // This is a simple check - in a real app, you'd check admin role in Firestore
      if (currentUser.email.includes('admin')) {
        setIsAdmin(true);
      }
    }
    
    fetchCompanies();
  }, [currentUser]);
  
  // Fetch existing companies from Firestore
  const fetchCompanies = async () => {
    try {
      const shiftsRef = collection(db, 'shiftPatterns');
      const querySnapshot = await getDocs(shiftsRef);
      
      const uniqueCompanies = [];
      const companyPatterns = {};
      const shiftsData = [];
      
      querySnapshot.forEach((docSnapshot) => {
        const rawData = docSnapshot.data();
        
        // Handle both field names (startDate and patternStartDate) that might be used in Firebase
        // This ensures compatibility with different data formats
        let dateField = rawData.startDate || rawData.patternStartDate;
        let processedStartDate = null;
        
        if (dateField) { 
          if (typeof dateField.toDate === 'function') {
            // Firestore Timestamp: convert to 'YYYY-MM-DD' string
            processedStartDate = dateField.toDate().toISOString().split('T')[0];
          } else if (typeof dateField === 'string') {
            // String: ensure it's 'YYYY-MM-DD' by taking the date part
            processedStartDate = dateField.split('T')[0].split(' ')[0];
          } else if (dateField instanceof Date) {
            // JavaScript Date object
            processedStartDate = dateField.toISOString().split('T')[0];
          } else {
            // Fallback for any other type
            console.warn(`AdminPanel: Date field for doc ${docSnapshot.id} has unexpected type: ${typeof dateField}`);
            processedStartDate = String(dateField);
          }
        }

        const data = { 
          id: docSnapshot.id, 
          ...rawData, 
          startDate: processedStartDate // Use the processed date consistently as startDate in our component
        };
        
        // Original logic for uniqueCompanies and companyPatterns:
        const companyName = data.company;
        const patternName = data.patternName;
        
        // Add checks for companyName to prevent errors if it's undefined/null
        if (companyName) {
          if (!uniqueCompanies.includes(companyName)) {
            uniqueCompanies.push(companyName);
          }
          
          if (!companyPatterns[companyName]) {
            companyPatterns[companyName] = [];
          }
          
          if (patternName && !companyPatterns[companyName].includes(patternName)) {
            companyPatterns[companyName].push(patternName);
          }
        }
        
        shiftsData.push(data);
      });
      
      setCompanies(uniqueCompanies);
      setPatterns(companyPatterns);
      setShifts(shiftsData);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setMessage({ text: 'Error fetching companies: ' + error.message, type: 'danger' });
    }
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Check if user selected "Add New Company" option
    if (name === 'company' && value === '__add_new__') {
      // Switch to the Companies tab
      setActiveTab('companies');
      return;
    }
    
    // Update form data
    setFormData({ ...formData, [name]: value });
    
    // If company changed, fetch the company logo
    if (name === 'company' && value) {
      fetchCompanyLogo(value);
    }
  };
  
  // Fetch company logo from the companies collection
  const fetchCompanyLogo = async (companyName) => {
    try {
      // Reset logo state
      setLogoFile(null);
      setLogoPreview(null);
      setFormData(prev => ({ ...prev, logoUrl: '' }));
      
      // Fetch company from Firestore
      const companiesRef = collection(db, 'companies');
      const q = query(companiesRef, where('name', '==', companyName));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const companyData = querySnapshot.docs[0].data();
        if (companyData.logoUrl) {
          console.log('Found logo for company:', companyName);
          // Set the logo URL in the form data
          setFormData(prev => ({ ...prev, logoUrl: companyData.logoUrl }));
          // Create a preview of the logo
          setLogoPreview(companyData.logoUrl);
        } else {
          console.log('No logo found for company:', companyName);
        }
      } else {
        console.log('Company not found in companies collection:', companyName);
      }
    } catch (error) {
      console.error('Error fetching company logo:', error);
    }
  };
  
  // Cycle through shift values (X → D → N → X) when clicking on a pattern input
  const cycleShiftValue = (index) => {
    const currentValue = formData.patternValues[index];
    let newValue;
    
    // Cycle through the values: X → D → N → X
    switch(currentValue) {
      case 'X':
        newValue = 'D';
        break;
      case 'D':
        newValue = 'N';
        break;
      case 'N':
      default:
        newValue = 'X';
        break;
    }
    
    // Update the pattern value
    const newPatternValues = [...formData.patternValues];
    newPatternValues[index] = newValue;
    setFormData({ ...formData, patternValues: newPatternValues });
  };
  
  // Handle pattern length change
  const handlePatternLengthChange = (e) => {
    const length = parseInt(e.target.value, 10);
    
    // Validate the length (ensure it's between 1 and 28)
    if (length < 1 || length > 28) {
      setMessage({ text: 'Pattern length must be between 1 and 28 days', type: 'danger' });
      return;
    }
    
    // Update pattern values array based on new length
    let newPatternValues = [...formData.patternValues];
    
    if (length > newPatternValues.length) {
      // Add new elements if length increased
      newPatternValues = [...newPatternValues, ...Array(length - newPatternValues.length).fill('X')];
    } else if (length < newPatternValues.length) {
      // Truncate array if length decreased
      newPatternValues = newPatternValues.slice(0, length);
    }
    
    setFormData({
      ...formData,
      patternLength: length,
      patternValues: newPatternValues 
    });
    
    // Clear any previous error messages
    if (message.type === 'danger') {
      setMessage({ text: '', type: '' });
    }
  };
  
  // Handle logo file selection
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      
      // Create a preview URL
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
    }
  };
  
  // This function has been removed as it's no longer needed
  // Logos are now managed at the company level through the CompanyManager component
  
  // Convert file to data URL function removed - now handled at company level
  // through the CompanyManager component
  
  // No need to delete logo from storage since we're using data URLs
  // The logo data is stored directly in Firestore and will be deleted with the document
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validate form data
      if (!formData.company || !formData.patternName || !formData.shift) {
        setMessage({ text: 'Please fill in all required fields', type: 'danger' });
        return;
      }
      
      // Create pattern data object
      const patternData = {
        company: formData.company,
        patternName: formData.patternName,
        shift: formData.shift,
        startDate: formData.startDate,
        shiftPattern: parseInt(formData.patternLength, 10), // Store the pattern length as a number
        pattern: formData.patternValues.slice(0, formData.patternLength), // Only store the actual pattern length
        logoUrl: logoPreview || '' // Use the preview URL (data URL)
      };
      
      // Get company logo from companies collection
      try {
        const companiesRef = collection(db, 'companies');
        const q = query(companiesRef, where('name', '==', formData.company));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const companyData = querySnapshot.docs[0].data();
          if (companyData.logoUrl) {
            console.log('Using logo from company:', formData.company);
            patternData.logoUrl = companyData.logoUrl;
          }
        }
      } catch (error) {
        console.error('Error fetching company logo:', error);
      }
      
      if (editMode && editId) {
        // Update existing pattern in Firestore
        await updateDoc(doc(db, 'shiftPatterns', editId), patternData);
        setMessage({ text: 'Shift pattern updated successfully!', type: 'success' });
        // Exit edit mode
        setEditMode(false);
        setEditId(null);
      } else {
        // Check if this shift pattern already exists (only for new patterns)
        const shiftsRef = collection(db, 'shiftPatterns');
        const q = query(
          shiftsRef, 
          where('company', '==', formData.company),
          where('patternName', '==', formData.patternName),
          where('shift', '==', formData.shift)
        );
        
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          setMessage({ text: 'This shift pattern already exists!', type: 'warning' });
          return;
        }
        
        // Add new pattern to Firestore
        await addDoc(collection(db, 'shiftPatterns'), patternData);
        setMessage({ text: 'Shift pattern added successfully!', type: 'success' });
      }
      
      // Reset form
      setFormData({
        company: '',
        patternName: '',
        shift: '',
        startDate: new Date().toISOString().split('T')[0],
        patternLength: 8,
        patternValues: Array(8).fill('X')
      });
      
      // Reset logo state
      setLogoFile(null);
      setLogoPreview(null);
      
      // Refresh companies list
      fetchCompanies();
    } catch (error) {
      console.error("Error saving shift pattern:", error);
      setMessage({ text: `Error ${editMode ? 'updating' : 'adding'} shift pattern: ` + error.message, type: 'danger' });
    }
  };
  
  // Handle edit pattern
  const handleEdit = (shift) => {
    // Set logo preview if a logo URL exists
    if (shift.logoUrl) {
      setLogoPreview(shift.logoUrl);
    } else {
      setLogoPreview(null);
    }
    
    // Reset logo file
    setLogoFile(null);
    
    // Get the date from either field name (startDate or patternStartDate)
    // This prevents errors if the data model changes
    const dateValue = shift.startDate || shift.patternStartDate || new Date().toISOString().split('T')[0];
    
    // Set form data with the selected shift pattern
    setFormData({
      company: shift.company,
      patternName: shift.patternName,
      shift: shift.shift,
      startDate: dateValue, // Use the normalized date value
      patternLength: shift.shiftPattern || shift.pattern?.length || 8,
      patternValues: shift.pattern || Array(shift.shiftPattern || 8).fill('X'),
      logoUrl: shift.logoUrl || ''
    });
    
    // Set edit mode and ID
    setEditMode(true);
    setEditId(shift.id);
    
    // Switch to the add tab to show the form
    setActiveTab('add');
    
    // Scroll to the top of the page
    window.scrollTo(0, 0);
  };
  
  // Handle pattern deletion
  const handleDelete = async (shiftId, logoUrl) => {
    if (window.confirm('Are you sure you want to delete this shift pattern?')) {
      try {
        // Delete from Firestore
        await deleteDoc(doc(db, 'shiftPatterns', shiftId));
        
        // No need to delete logo from storage since we're using data URLs
        // The logo data is stored in the companies collection, not with each pattern
        
        setMessage({ text: 'Shift pattern deleted successfully!', type: 'success' });
        
        // Refresh patterns list
        fetchCompanies();
      } catch (error) {
        console.error("Error deleting pattern:", error);
        setMessage({ text: 'Error deleting pattern: ' + error.message, type: 'danger' });
      }
    }
  };
  
  // Render pattern input fields
  const renderPatternInputs = () => {
    return (
      <div className="pattern-inputs mt-3">
        <div className="d-flex flex-wrap">
          {Array.from({ length: formData.patternLength }).map((_, index) => (
            <div key={index} className="pattern-day m-1">
              {/* Clickable shift value box that cycles through X, D, N values */}
              <div 
                className="shift-value-box"
                onClick={() => cycleShiftValue(index)}
                style={{
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  border: '1px solid #ced4da',
                  cursor: 'pointer',
                  userSelect: 'none',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  backgroundColor: 
                    formData.patternValues[index] === 'D' ? '#fff3cd' : 
                    formData.patternValues[index] === 'N' ? '#e9ecef' : '#ffffff',
                  transition: 'background-color 0.2s, transform 0.1s'
                }}
              >
                {formData.patternValues[index]}
              </div>
              <small className="d-block text-center">{index + 1}</small>
            </div>
          ))}
        </div>
        <div className="mt-3">
          <small className="text-muted">
            <strong>Click on any box to cycle through:</strong> X (day off) → D (day shift) → N (night shift) → X
          </small>
        </div>
      </div>
    );
  };
  
  // If not admin, show access denied message
  if (!isAdmin) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Access Denied</Alert.Heading>
          <p>
            You don't have permission to access the admin panel. Please contact an administrator.
          </p>
        </Alert>
      </Container>
    );
  }
  
  return (
    <Container className="mt-4">
      <h1 className="mb-4">Company Shift Pattern Admin</h1>
      
      {message.text && (
        <Alert variant={message.type} onClose={() => setMessage({ text: '', type: '' })} dismissible>
          {message.text}
        </Alert>
      )}
      
      <Tabs 
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
      >
        <Tab eventKey="companies" title="Manage Companies">
          <CompanyManager />
        </Tab>
        <Tab eventKey="add" title="Add Shift Pattern">
          <Card>
            <Card.Header>Add New Shift Pattern</Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Company Name</Form.Label>
                      <Form.Select
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select a company</option>
                        {companies.map((company, index) => (
                          <option key={index} value={company}>{company}</option>
                        ))}
                        <option value="__add_new__">+ Add New Company</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Select an existing company or add a new one
                      </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Company Logo</Form.Label>
                      <div className="d-flex flex-column">
                        {logoPreview && (
                          <div className="mb-2">
                            <img 
                              src={logoPreview} 
                              alt="Logo Preview" 
                              style={{ maxWidth: '100%', maxHeight: '100px' }} 
                              className="border rounded p-1"
                            />
                          </div>
                        )}
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleLogoChange}
                          disabled={isUploading}
                        />
                        <Form.Text className="text-muted">
                          Upload a company logo image (PNG, JPG, GIF)
                        </Form.Text>
                        {isUploading && (
                          <div className="mt-2">
                            <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            <span>Uploading logo...</span>
                          </div>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                  
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Pattern Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="patternName"
                        value={formData.patternName}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter pattern name (e.g., 4-on-4-off)"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Shift Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="shift"
                        value={formData.shift}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter shift name (e.g., A, B, Green)"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <SimpleDatePicker
                        label="Pattern Start Date"
                        selectedDate={formData.startDate}
                        onDateChange={(date) => setFormData({ ...formData, startDate: date })}
                      />
                      <Form.Text className="text-muted">
                        The date when day 1 of the pattern begins
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Pattern Length (days)</Form.Label>
                      <Form.Control
                        type="number"
                        name="patternLength"
                        min="1"
                        max="56"
                        value={formData.patternLength}
                        onChange={handlePatternLengthChange}
                        required
                      />
                      <Form.Text className="text-muted">
                        Number of days before the pattern repeats
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Form.Group className="mb-3">
                  <Form.Label>Shift Pattern</Form.Label>
                  {renderPatternInputs()}
                </Form.Group>
                
                <div className="d-flex gap-2">
                  <Button variant="primary" type="submit">
                    {editMode ? 'Update Shift Pattern' : 'Add Shift Pattern'}
                  </Button>
                  {editMode && (
                    <Button variant="secondary" onClick={() => {
                      // Cancel edit mode
                      setEditMode(false);
                      setEditId(null);
                      // Reset form
                      setFormData({
                        company: '',
                        patternName: '',
                        shift: '',
                        startDate: new Date().toISOString().split('T')[0],
                        patternLength: 28,
                        patternValues: Array(28).fill('X')
                      });
                    }}>
                      Cancel Edit
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="manage" title="Manage Existing Patterns">
          <Card>
            <Card.Header>Existing Shift Patterns</Card.Header>
            <Card.Body>
              {shifts.length > 0 ? (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Pattern</th>
                      <th>Shift</th>
                      <th>Start Date</th>
                      <th>Length</th>
                      <th>Pattern Preview</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shifts.map((shift) => (
                      <tr key={shift.id}>
                        <td>{shift.company}</td>
                        <td>{shift.patternName}</td>
                        <td>{shift.shift}</td>
                        <td>{shift.startDate || ''}</td>
                        <td>{shift.shiftPattern}</td>
                        <td>
                          <div className="d-flex" style={{ maxWidth: '200px', overflowX: 'auto' }}>
                            {shift.pattern && shift.pattern.map((item, index) => {
                              // Handle both simple string values and objects with {day, value} or {value, day} structure
                              const displayValue = typeof item === 'object' ? (item.value || '') : item;
                              
                              return (
                                <div 
                                  key={index}
                                  style={{
                                    width: '25px',
                                    height: '25px',
                                    margin: '1px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.8rem',
                                    backgroundColor: displayValue === 'D' ? '#fff3cd' : displayValue === 'N' ? '#e9ecef' : '#ffffff',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px'
                                  }}
                                >
                                  {displayValue}
                                </div>
                              );
                            })}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button 
                              variant="primary" 
                              size="sm"
                              onClick={() => handleEdit(shift)}
                            >
                              Edit
                            </Button>
                            <Button 
                              variant="danger" 
                              size="sm"
                              onClick={() => handleDelete(shift.id, shift.logoUrl)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="info">
                  No shift patterns found. Add some using the "Add Shift Pattern" tab.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </Container>
  );
};

export default AdminPanel;
